import React, { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import './App.css';

type Material = {
  id: number;
  name: string;
  image: string;
  color: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
};

type MaterialForm = {
  name: string;
  image: string;
  color: string;
  quantity: string;
};

const initialMaterials: Material[] = [
  {
    id: 1,
    name: '棉布',
    image: 'cotton.png',
    color: '白色',
    quantity: 120,
    createdAt: '2026-05-01 09:00:00',
    updatedAt: '2026-05-01 09:00:00',
  },
  {
    id: 2,
    name: '拉链',
    image: 'zipper.png',
    color: '黑色',
    quantity: 300,
    createdAt: '2026-05-03 10:30:00',
    updatedAt: '2026-05-03 10:30:00',
  },
];

const emptyForm: MaterialForm = {
  name: '',
  image: '',
  color: '',
  quantity: '',
};

function getCurrentTime() {
  return new Date().toLocaleString('zh-CN', { hour12: false });
}

function App() {
  const [materials, setMaterials] = useState<Material[]>(initialMaterials);
  const [form, setForm] = useState<MaterialForm>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchName, setSearchName] = useState('');
  const [appliedSearchName, setAppliedSearchName] = useState('');

  const displayedMaterials = useMemo(() => {
    if (!appliedSearchName) {
      return materials;
    }

    return materials.filter(
      (material) =>
        material.name.includes(appliedSearchName) || material.color.includes(appliedSearchName),
    );
  }, [materials, appliedSearchName]);

  const handleFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const now = getCurrentTime();

    if (editingId !== null) {
      setMaterials((currentMaterials) =>
        currentMaterials.map((material) =>
          material.id === editingId
            ? {
                ...material,
                name: form.name,
                image: material.image,
                color: form.color,
                quantity: Number(form.quantity),
                updatedAt: now,
              }
            : material,
        ),
      );
      setEditingId(null);
      setForm(emptyForm);
      return;
    }

    const nextMaterial: Material = {
      id: Date.now(),
      name: form.name,
      image: form.image,
      color: form.color,
      quantity: Number(form.quantity),
      createdAt: now,
      updatedAt: now,
    };

    setMaterials((currentMaterials) => [...currentMaterials, nextMaterial]);
    setForm(emptyForm);
  };

  const handleEdit = (material: Material) => {
    setEditingId(material.id);
    setForm({
      name: material.name,
      image: material.image,
      color: material.color,
      quantity: String(material.quantity),
    });
  };

  const handleDelete = (id: number) => {
    setMaterials((currentMaterials) => currentMaterials.filter((material) => material.id !== id));
  };

  const handleSearch = () => {
    setAppliedSearchName(searchName);
  };

  const handleReset = () => {
    setSearchName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  return (
    <main className="page">
      <section className="panel">
        <div className="page-title">
          <h1>物料管理</h1>
          <p>管理员可以维护物料信息，并按照名称查询物料。</p>
        </div>

        <form className="material-form" onSubmit={handleSubmit}>
          <label>
            名称
            <input
              name="name"
              value={form.name}
              onChange={handleFormChange}
              placeholder="请输入物料名称"
              required
            />
          </label>
          <label>
            图片
            <input
              name="image"
              value={form.image}
              onChange={handleFormChange}
              placeholder="请输入图片地址"
              required
            />
          </label>
          <label>
            颜色
            <input
              name="color"
              value={form.color}
              onChange={handleFormChange}
              placeholder="请输入颜色"
              required
            />
          </label>
          <label>
            数量
            <input
              name="quantity"
              type="number"
              value={form.quantity}
              onChange={handleFormChange}
              placeholder="请输入数量"
              required
            />
          </label>

          <div className="form-actions">
            <button type="submit">{editingId === null ? '添加物料' : '保存修改'}</button>
            {editingId !== null && (
              <button type="button" className="secondary-button" onClick={handleCancelEdit}>
                取消
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="panel">
        <div className="toolbar">
          <label>
            名称搜索
            <input
              value={searchName}
              onChange={(event) => setSearchName(event.target.value)}
              placeholder="输入名称关键字"
            />
          </label>
          <button type="button" onClick={handleSearch}>
            查询
          </button>
          <button type="button" className="secondary-button" onClick={handleReset}>
            重置
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>名称</th>
                <th>图片</th>
                <th>颜色</th>
                <th>数量</th>
                <th>创建时间</th>
                <th>修改时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {displayedMaterials.map((material) => (
                <tr key={material.id}>
                  <td>{material.id}</td>
                  <td>{material.name}</td>
                  <td>{material.image}</td>
                  <td>{material.color}</td>
                  <td>{material.quantity}</td>
                  <td>{material.createdAt}</td>
                  <td>{material.updatedAt}</td>
                  <td>
                    <button type="button" onClick={() => handleEdit(material)}>
                      修改
                    </button>
                    <button
                      type="button"
                      className="danger-button"
                      onClick={() => handleDelete(material.id)}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
              {displayedMaterials.length === 0 && (
                <tr>
                  <td className="empty-cell" colSpan={8}>
                    暂无物料
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

export default App;
